import { useCallback, useState, type FormEvent } from 'react';
import type { QaFinding, QaRule, QaRun, QaRunDetail, QaScore, QaUtterance } from '@arcnet/contracts';
import {
  useAcceptQaTerms, useDeleteQaRule, useQaConsents, useQaKeys, useQaRules, useQaRun, useQaRuns,
  useQaSettings, useQaUsage, useQaWhoami, useRemoveQaKey, useSaveQaKey, useSaveQaRule,
  useSaveQaSettings, useWithdrawQaTerms, useQaScorecards, useOverrideQaScore,
} from '../../lib/api/hooks-qa.js';
import { Guidance, Scorecards } from './Scorecards.js';
import { QaReports, QaSearch } from './QaReports.js';
import { useAdminGroups } from '../../lib/api/hooks-extension.js';
import { useQueues } from '../../lib/api/hooks-tms.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { useSession } from '../../lib/auth/session.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { useSubroute } from '../../lib/subroute.js';
import { formatDuration } from '../../lib/format.js';
import {
  Badge, CardHead, Empty, ErrorBanner, SectionHead, Stat, StatGroup, TableSkeleton, Tab, Tabs,
} from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { RecordingPlayer } from '../cdrs/RecordingPlayer.js';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * Auto QA (docs/37): calls transcribed and analysed on the account's own provider keys.
 *
 * Each view is an address: the calls that have been through it, reports and search across them, the rules that choose them,
 * and the setup that has to be in place before any call can leave for a provider -- the keys,
 * the dated acceptance, the monthly ceiling.
 *
 * The words of a call are the client's alone (0119). Somebody above the account sees the list, the
 * costs and the scores, and is told plainly that the transcript is not theirs to read -- never an
 * empty transcript, which would look like a silent call.
 */

type View = 'calls' | 'reports' | 'search' | 'rules' | 'scorecards' | 'prompts' | 'setup';
const VIEWS: ReadonlyArray<readonly [View, string]> = [
  ['calls', 'Calls'], ['reports', 'Reports'], ['search', 'Search'], ['rules', 'Rules'], ['scorecards', 'Scorecards'], ['prompts', 'Prompts'], ['setup', 'Setup'],
];

const WORKING = ['queued', 'transcribing', 'analysing'];

export function AutoQaPage({ tenantId }: { tenantId: string }) {
  const route = useSubroute();
  const view = (VIEWS.find(([v]) => v === route.rest[0])?.[0]) ?? 'calls';
  const settings = useQaSettings(tenantId);
  const whoami = useQaWhoami(tenantId);
  // Search reads the words: only the account's own people are offered it (docs/37 §5).
  const views = VIEWS.filter(([v]) => v !== 'search' || whoami.data?.reads_words);
  const [open, setOpen] = useState<string | null>(null);

  if (open) return <RunView tenantId={tenantId} id={open} onBack={() => setOpen(null)} />;

  const s = settings.data;
  return (
    <>
      <SectionHead title="Auto QA"
        sub="Calls transcribed and analysed on this account's own provider keys." />

      {/* Problems only: the one reason the pipeline is waiting, said once for the account. */}
      {s?.paused_reason && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>Auto QA is waiting: {s.paused_reason} Queued calls run once this is fixed.</span>
          {view !== 'setup' && (
            <button type="button" className="arc-btn arc-btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => route.go(['setup'])}>Open setup</button>
          )}
        </div>
      )}

      <Tabs style={{ marginBottom: 14 }}>
        {views.map(([v, label]) => (
          <Tab key={v} active={view === v} onSelect={() => route.go([v])}>{label}</Tab>
        ))}
      </Tabs>

      {settings.error && <ErrorBanner error={settings.error} />}
      {view === 'calls' && <Runs tenantId={tenantId} onOpen={setOpen} />}
      {view === 'reports' && <QaReports tenantId={tenantId} />}
      {view === 'search' && whoami.data?.reads_words && <QaSearch tenantId={tenantId} onOpen={setOpen} />}
      {view === 'rules' && <Rules tenantId={tenantId} />}
      {view === 'scorecards' && <Scorecards tenantId={tenantId} />}
      {view === 'prompts' && <Guidance tenantId={tenantId} />}
      {view === 'setup' && <Setup tenantId={tenantId} />}
    </>
  );
}

// ================================================================================== the calls

const sentimentTone = (v: number | null): 'ok' | 'warn' | 'danger' | 'muted' =>
  v === null ? 'muted' : v >= 0.25 ? 'ok' : v <= -0.25 ? 'danger' : 'warn';
const sentimentWord = (v: number | null): string =>
  v === null ? '—' : v >= 0.25 ? 'positive' : v <= -0.25 ? 'negative' : 'neutral';

function Runs({ tenantId, onOpen }: { tenantId: string; onOpen: (id: string) => void }) {
  const runs = useQaRuns(tenantId);
  const usage = useQaUsage(tenantId);
  const u = usage.data;

  return (
    <>
      {u && (
        <StatGroup>
          <Stat bare label="This month" value={`${u.month_minutes} min`} sub={`of ${u.cap_minutes}`} />
          <Stat bare label="Calls done" value={String(u.runs_this_month)} />
          <Stat bare label="Waiting" value={String(u.queued)} />
          <Stat bare label="Extensions on Auto QA" value={String(u.enabled_seats)} />
        </StatGroup>
      )}

      <div className="arc-card" style={{ marginTop: 14 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Call</th><th>Extension</th><th>With</th>
                <th className="arc-td-right">Length</th><th className="arc-td-right">Mark</th>
                <th>Sentiment</th><th>Status</th>
              </tr>
            </thead>
            {runs.data === undefined ? <TableSkeleton cols={7} /> : (
              <tbody>
                {runs.data.data.map((r) => (
                  <tr key={r.id} className="arc-clickable" onClick={() => onOpen(r.id)}>
                    <td className="arc-num">
                      {new Date(r.cdr_started_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
                    </td>
                    <td>{r.extension_number ?? '—'}</td>
                    <td className="arc-mono">{r.party ?? '—'}</td>
                    <td className="arc-td-right arc-num">{r.duration_sec != null ? formatDuration(r.duration_sec) : '—'}</td>
                    <td className="arc-td-right arc-num">
                      {r.score == null ? <span className="arc-subtle">—</span>
                        : r.auto_failed ? <Badge tone="danger">failed</Badge>
                          : <span style={{ fontWeight: 600 }}>{Math.round(r.score)}</span>}
                      {r.findings_high > 0 && <> <Badge tone="warn">{r.findings_high} high</Badge></>}
                    </td>
                    <td>
                      {r.state === 'complete'
                        ? <Badge tone={sentimentTone(r.sentiment)}>{sentimentWord(r.sentiment)}</Badge>
                        : <span className="arc-subtle">—</span>}
                    </td>
                    <td><RunState run={r} /></td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {runs.error && <ErrorBanner error={runs.error} />}
        {runs.data && runs.data.data.length === 0 && (
          <Empty icon="phone" title="No calls yet"
            hint="Calls a rule chooses appear here once their recording is stored. A call can also be sent from its page in call history." />
        )}
      </div>
    </>
  );
}

function RunState({ run }: { run: Pick<QaRun, 'state' | 'error'> }) {
  if (run.state === 'complete') return <Badge tone="ok">done</Badge>;
  if (run.state === 'failed') return <span title={run.error ?? undefined}><Badge tone="danger">failed</Badge></span>;
  if (WORKING.includes(run.state)) return <Badge tone="info">{run.state === 'queued' ? 'waiting' : run.state}</Badge>;
  return <Badge tone="muted">{run.state}</Badge>;
}

/**
 * One call: the recording beside what was said, the line being played highlighted, and every
 * line a place to play from. The sentiment's turning points point at their lines.
 */
function RunView({ tenantId, id, onBack }: { tenantId: string; id: string; onBack: () => void }) {
  const run = useQaRun(tenantId, id);
  const [at, setAt] = useState(0);
  const [seekTo, setSeekTo] = useState<{ sec: number; n: number } | null>(null);
  const onTime = useCallback((sec: number) => setAt(sec), []);
  const play = (ms: number) => setSeekTo((p) => ({ sec: ms / 1000, n: (p?.n ?? 0) + 1 }));

  const r = run.data;
  const lines = r?.utterances ?? [];
  const current = lines.findIndex((l) => at * 1000 >= l.start_ms && at * 1000 < l.end_ms + 400);
  const inbound = r?.direction === 'inbound';
  const lanes: [string, string] = inbound ? ['Caller', 'Agent'] : ['Agent', 'Other party'];

  return (
    <>
      <button type="button" className="arc-crumb" onClick={onBack}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <Icon name="arrowLeft" size={15} /> Auto QA
      </button>
      {run.error && <ErrorBanner error={run.error} />}
      {!r ? <TableSkeleton cols={2} /> : (
        <>
          <SectionHead
            title={`${r.extension_number ?? 'Call'} · ${r.party ?? ''}`}
            sub={`${new Date(r.cdr_started_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}`
              + `${r.duration_sec != null ? ` · ${formatDuration(r.duration_sec)}` : ''}`
              + `${r.transcribe_model ? ` · ${r.transcribe_model}` : ''}${r.analysis_model ? ` · ${r.analysis_model}` : ''}`}
            action={<RunState run={r} />} />

          {r.state === 'failed' && r.error && (
            <div className="arc-banner arc-banner-warn" style={{ marginBottom: 12 }}>
              <Icon name="warning" size={14} /><span>{r.error}</span>
            </div>
          )}

          <RecordingPlayer tenantId={tenantId} callId={r.cdr_id} status="ready" laneNames={lanes}
            expectedSha256={null} format={null} bytes={null} onTime={onTime} seekTo={seekTo} />

          {r.scores.length > 0 && (
            <ScoresCard tenantId={tenantId} run={r} lines={lines} onPlay={play} />
          )}

          {r.findings && r.findings.length > 0 && (
            <FindingsCard findings={r.findings} lines={lines} onPlay={play} />
          )}

          {r.sentiment_result && (
            <div className="arc-card arc-card-body" style={{ marginTop: 14 }}>
              <div className="arc-row" style={{ gap: 18, flexWrap: 'wrap', marginBottom: 8 }}>
                <Score label="Overall" v={r.sentiment_result.overall} />
                <Score label="Agent" v={r.sentiment_result.agent} />
                <Score label="Caller" v={r.sentiment_result.caller} />
              </div>
              {r.sentiment_result.summary && <p style={{ fontSize: 13 }}>{r.sentiment_result.summary}</p>}
              {r.sentiment_result.turns.length > 0 && (
                <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12.5 }}>
                  {r.sentiment_result.turns.map((t) => {
                    const line = lines[t.seq];
                    return (
                      <li key={t.seq} style={{ marginBottom: 4 }}>
                        <Badge tone={t.direction === 'better' ? 'ok' : 'danger'}>{t.direction}</Badge>{' '}
                        {t.why}{' '}
                        {line && (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            onClick={() => play(line.start_ms)}>
                            at {clock(line.start_ms)}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div className="arc-card" style={{ marginTop: 14 }}>
            <CardHead title="Transcript" />
            <div className="arc-card-body">
              {r.words === 'client_only' && (
                <p className="arc-muted" style={{ fontSize: 12.5 }}>
                  What was said on a client's call is the client's alone: their own users can read
                  it, and nobody above the account can, whatever they hold.
                </p>
              )}
              {r.words === 'deleted' && (
                <p className="arc-muted" style={{ fontSize: 12.5 }}>
                  The transcript was deleted with the recording under the account's retention
                  {r.words_deleted_at ? ` on ${new Date(r.words_deleted_at).toLocaleDateString('en-ZA')}` : ''}.
                  The scores are kept.
                </p>
              )}
              {r.words === 'none' && WORKING.includes(r.state) && (
                <p className="arc-muted" style={{ fontSize: 12.5 }}>Still working on this call.</p>
              )}
              {r.words === 'none' && r.state === 'complete' && (
                <p className="arc-muted" style={{ fontSize: 12.5 }}>Nobody spoke on this call.</p>
              )}
              {lines.map((l, i) => (
                <Line key={l.seq} line={l} current={i === current} onPlay={() => play(l.start_ms)} />
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const clock = (ms: number): string => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * The scorecard's answers, each with its reason and the lines it cited -- and a supervisor's own
 * answer beside the model's, never over it (docs/37 §8). The mark is worked out again from
 * whatever the answers now are.
 */
function ScoresCard({ tenantId, run, lines, onPlay }: {
  tenantId: string; run: QaRunDetail; lines: QaUtterance[]; onPlay: (ms: number) => void;
}) {
  const can = useSession((s) => s.can);
  const override = useOverrideQaScore(tenantId, run.id);
  const passed = run.score != null && run.pass_mark != null && !run.auto_failed && run.score >= run.pass_mark;

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <CardHead title={run.scorecard_name ?? 'Scorecard'} action={run.score != null && (
        <span className="arc-row" style={{ gap: 8 }}>
          <span className="arc-num" style={{ fontSize: 20, fontWeight: 650 }}>{Math.round(run.score)}</span>
          <Badge tone={run.auto_failed ? 'danger' : passed ? 'ok' : 'warn'}>
            {run.auto_failed ? 'failed on an auto-fail question' : passed ? 'passed' : `below ${run.pass_mark}`}
          </Badge>
        </span>
      )} />
      <div className="arc-card-body">
        {run.scores.map((sc) => (
          <ScoreRow key={sc.question_id} sc={sc} lines={lines} onPlay={onPlay}
            mayOverride={can('qa:write')} saving={override.isPending}
            onOverride={(score, note) => override.mutate({ questionId: sc.question_id, score, note })} />
        ))}
        {override.error && <ErrorBanner error={override.error} />}
      </div>
    </div>
  );
}

const answerText = (v: number | null, scale: QaScore['scale']): string =>
  v == null ? 'n/a' : scale === 'yes_no' ? (v >= 1 ? 'Yes' : 'No') : `${Math.round(v * 5)} / 5`;

function ScoreRow({ sc, lines, onPlay, mayOverride, saving, onOverride }: {
  sc: QaScore; lines: QaUtterance[]; onPlay: (ms: number) => void;
  mayOverride: boolean; saving: boolean; onOverride: (score: number | null, note: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(sc.override_score ?? sc.score ?? ''));
  const [note, setNote] = useState(sc.override_note ?? '');
  const final = sc.override_score ?? sc.score;
  const ok = final == null ? null : final >= 1;

  return (
    <div style={{ borderTop: '1px solid var(--arc-border)', padding: '10px 0' }}>
      <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 550 }}>
            {sc.question}
            {sc.auto_fail && <span className="arc-subtle" style={{ fontWeight: 400 }}> · auto-fail</span>}
          </div>
          {sc.reason && <div className="arc-muted" style={{ fontSize: 12, marginTop: 3 }}>{sc.reason}</div>}
          {sc.evidence.length > 0 && (
            <div className="arc-row" style={{ gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
              {sc.evidence.map((seq) => lines[seq] ? (
                <button key={seq} type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => onPlay(lines[seq]!.start_ms)}>{clock(lines[seq]!.start_ms)}</button>
              ) : null)}
            </div>
          )}
          {sc.override_score != null && (
            <div className="arc-subtle" style={{ fontSize: 11.5, marginTop: 4 }}>
              Changed by {sc.overridden_by_name ?? 'a supervisor'} from {answerText(sc.score, sc.scale)}
              {sc.override_note ? `: ${sc.override_note}` : ''}
            </div>
          )}
        </div>
        <Badge tone={ok == null ? 'muted' : ok ? 'ok' : 'danger'}>{answerText(final, sc.scale)}</Badge>
        {mayOverride && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Change'}
          </button>
        )}
      </div>
      {editing && (
        <form className="arc-row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            onOverride(value === '' ? null : Number(value), note.trim() || null);
            setEditing(false);
          }}>
          <div className="arc-field" style={{ margin: 0, width: 150 }}>
            <label className="arc-label" htmlFor={`ov-${sc.question_id}`}>Your answer</label>
            <select id={`ov-${sc.question_id}`} className="arc-select" value={value} onChange={(e) => setValue(e.target.value)}>
              {sc.scale === 'yes_no'
                ? <><option value="1">Yes</option><option value="0">No</option></>
                : [0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={String(n / 5)}>{n} / 5</option>)}
            </select>
          </div>
          <div className="arc-field" style={{ margin: 0, flex: 1, minWidth: 200 }}>
            <label className="arc-label" htmlFor={`on-${sc.question_id}`}>Why</label>
            <input id={`on-${sc.question_id}`} className="arc-input" value={note} maxLength={1000}
              onChange={(e) => setNote(e.target.value)} />
          </div>
          <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={saving}>Save</button>
          {sc.override_score != null && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={saving}
              onClick={() => { onOverride(null, null); setEditing(false); }}>
              Use the model's answer
            </button>
          )}
        </form>
      )}
    </div>
  );
}

/** What the technical and compliance analyses found, each linked to the line it came from. */
function FindingsCard({ findings, lines, onPlay }: {
  findings: QaFinding[]; lines: QaUtterance[]; onPlay: (ms: number) => void;
}) {
  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <CardHead title="Findings" />
      <div className="arc-card-body">
        {findings.map((f) => {
          const line = f.seq != null ? lines[f.seq] : undefined;
          return (
            <div key={f.id} className="arc-row" style={{ gap: 10, alignItems: 'flex-start', padding: '6px 0' }}>
              <Badge tone={f.severity === 'high' ? 'danger' : f.severity === 'low' ? 'warn' : 'muted'}>{f.severity}</Badge>
              <div style={{ flex: 1, fontSize: 12.5 }}>
                <span className="arc-subtle">{f.kind === 'compliance' ? 'Compliance' : 'Handling'}: </span>
                {f.what}
                {f.suggestion && <div className="arc-muted" style={{ fontSize: 12, marginTop: 2 }}>{f.suggestion}</div>}
              </div>
              {line && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => onPlay(line.start_ms)}>
                  {clock(line.start_ms)}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Score({ label, v }: { label: string; v: number }) {
  return (
    <div>
      <div className="arc-stat-label">{label}</div>
      <Badge tone={sentimentTone(v)}>{sentimentWord(v)} {v.toFixed(2)}</Badge>
    </div>
  );
}

function Line({ line, current, onPlay }: { line: QaUtterance; current: boolean; onPlay: () => void }) {
  return (
    <div className="arc-row" onClick={onPlay} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onPlay(); }}
      style={{
        gap: 10, alignItems: 'flex-start', padding: '6px 8px', borderRadius: 'var(--arc-r)', cursor: 'pointer',
        background: current ? 'var(--arc-surface-2)' : undefined,
      }}>
      <span className="arc-subtle arc-num" style={{ fontSize: 11.5, minWidth: 38, paddingTop: 2 }}>{clock(line.start_ms)}</span>
      <span style={{ fontSize: 11.5, fontWeight: 600, minWidth: 52, paddingTop: 2 }}
        className={line.speaker === 'agent' ? undefined : 'arc-muted'}>
        {line.speaker === 'agent' ? 'Agent' : 'Caller'}
      </span>
      <span style={{ fontSize: 13, lineHeight: 1.5 }}>{line.text}</span>
    </div>
  );
}

// ================================================================================== rules

const SCOPES = [['account', 'The whole account'], ['extensions', 'Named extensions'],
  ['department', 'A department'], ['queue', 'A queue’s agents']] as const;

function Rules({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const rules = useQaRules(tenantId);
  const del = useDeleteQaRule(tenantId);
  const [editing, setEditing] = useState<QaRule | 'new' | null>(null);

  return (
    <>
      <SectionHead title="Rules" sub="Which recorded calls go through Auto QA. A call matching two rules is done once."
        action={can('qa:write') && !editing
          ? <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setEditing('new')}>
              <Icon name="plus" size={13} /> Rule
            </button>
          : undefined} />
      {editing && (
        <RuleForm tenantId={tenantId} existing={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      )}
      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>Rule</th><th>Calls</th><th>Direction</th><th className="arc-td-right">Over</th>
                <th className="arc-td-right">Sample</th><th /></tr>
            </thead>
            {rules.data === undefined ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rules.data.data.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{r.name}</span>
                      {!r.enabled && <> <Badge tone="muted">off</Badge></>}
                    </td>
                    <td>{SCOPES.find(([k]) => k === r.scope)?.[1]}{r.scope !== 'account' ? ` (${r.scope_ids.length})` : ''}</td>
                    <td>{r.direction === 'any' ? 'Both' : r.direction === 'inbound' ? 'Received' : 'Placed'}</td>
                    <td className="arc-td-right arc-num">{r.min_duration_sec ? formatDuration(r.min_duration_sec) : '—'}</td>
                    <td className="arc-td-right arc-num">{r.sample_percent}%</td>
                    <td className="arc-td-right">
                      {can('qa:write') && (
                        <>
                          <RowAction kind="edit" label={`Edit ${r.name}`} onClick={() => setEditing(r)} />
                          <RowAction kind="delete" label={`Delete ${r.name}`} disabled={del.isPending}
                            onClick={() => { void confirmAction({ title: `Delete ${r.name}?`, body: 'Calls it chose keep their results.', action: 'Delete', danger: true }).then((ok) => { if (ok) del.mutate(r.id); }); }} />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {rules.data && rules.data.data.length === 0 && (
          <Empty icon="queue" title="No rules"
            hint="Without a rule, only calls sent by hand from call history are done." />
        )}
      </div>
    </>
  );
}

function RuleForm({ tenantId, existing, onDone }: { tenantId: string; existing: QaRule | null; onDone: () => void }) {
  const save = useSaveQaRule(tenantId);
  const extensions = useExtensions(tenantId, { limit: 500 });
  const groups = useAdminGroups(tenantId);
  const queues = useQueues(tenantId);

  const [name, setName] = useState(existing?.name ?? '');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [scope, setScope] = useState<QaRule['scope']>(existing?.scope ?? 'account');
  const [ids, setIds] = useState<string[]>(existing?.scope_ids ?? []);
  const [direction, setDirection] = useState<QaRule['direction']>(existing?.direction ?? 'any');
  const [minMin, setMinMin] = useState(existing ? String(Math.round(existing.min_duration_sec / 60)) : '0');
  const [sample, setSample] = useState(String(existing?.sample_percent ?? 100));
  const [analyses, setAnalyses] = useState<string[]>(existing?.analyses ?? ['sentiment']);
  const [cardId, setCardId] = useState<string>(existing?.scorecard_id ?? '');
  const cards = useQaScorecards(tenantId);

  const choices: Array<{ id: string; label: string }> =
    scope === 'extensions' ? (extensions.data?.data ?? []).map((e) => ({ id: e.id, label: `${e.number} ${e.display_name ?? ''}` }))
      : scope === 'department' ? (groups.data?.data ?? []).map((g) => ({ id: g.id, label: g.name }))
        : scope === 'queue' ? (queues.data?.data ?? []).map((q) => ({ id: q.id, label: q.name }))
          : [];
  const toggle = (id: string) => setIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name, enabled, scope, scope_ids: scope === 'account' ? [] : ids, direction,
          min_duration_sec: Math.max(0, Number(minMin) || 0) * 60,
          sample_percent: Math.min(100, Math.max(1, Number(sample) || 100)),
          analyses: analyses.length > 0 ? analyses : ['sentiment'],
          scorecard_id: cardId || null,
        }, { onSuccess: onDone });
      }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="qr-name">Name</label>
          <input id="qr-name" className="arc-input" value={name} maxLength={120} required
            onChange={(e) => setName(e.target.value)} placeholder="Sales calls over 3 minutes" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="qr-scope">Calls of</label>
          <select id="qr-scope" className="arc-select" value={scope}
            onChange={(e) => { setScope(e.target.value as QaRule['scope']); setIds([]); }}>
            {SCOPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="qr-dir">Direction</label>
          <select id="qr-dir" className="arc-select" value={direction}
            onChange={(e) => setDirection(e.target.value as QaRule['direction'])}>
            <option value="any">Placed and received</option>
            <option value="inbound">Received</option>
            <option value="outbound">Placed</option>
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="qr-min">Longer than (minutes)</label>
          <input id="qr-min" className="arc-input arc-num" inputMode="numeric" value={minMin}
            onChange={(e) => setMinMin(e.target.value.replace(/[^0-9]/g, ''))} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="qr-sample">Sample (%)</label>
          <input id="qr-sample" className="arc-input arc-num" inputMode="numeric" value={sample}
            onChange={(e) => setSample(e.target.value.replace(/[^0-9]/g, ''))} />
          <div className="arc-hint">100 does every call that matches.</div>
        </div>
      </div>

      <div className="arc-row" style={{ gap: 16, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <span className="arc-label">Analyses</span>
          <div className="arc-row" style={{ gap: 12 }}>
            {([['sentiment', 'Sentiment'], ['technical', 'Handling'], ['compliance', 'Compliance']] as const).map(([k, l]) => (
              <label key={k} className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
                <input type="checkbox" checked={analyses.includes(k)}
                  onChange={(e) => setAnalyses((p) => e.target.checked ? [...p, k] : p.filter((x) => x !== k))} />
                {l}
              </label>
            ))}
          </div>
        </div>
        <div className="arc-field" style={{ margin: 0, minWidth: 220 }}>
          <label className="arc-label" htmlFor="qr-card">Mark against</label>
          <select id="qr-card" className="arc-select" value={cardId} onChange={(e) => setCardId(e.target.value)}>
            <option value="">No scorecard</option>
            {(cards.data?.data ?? []).filter((c) => c.enabled || c.id === cardId)
              .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {scope !== 'account' && (
        <div className="arc-field">
          <span className="arc-label">Choose</span>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 5, border: '1px solid var(--arc-border)',
            borderRadius: 'var(--arc-r)', padding: 9, maxHeight: 160, overflowY: 'auto',
          }}>
            {choices.map((c) => (
              <button key={c.id} type="button"
                className={`arc-btn arc-btn-sm${ids.includes(c.id) ? ' arc-btn-primary' : ''}`}
                onClick={() => toggle(c.id)}>{c.label}</button>
            ))}
            {choices.length === 0 && <span className="arc-subtle" style={{ fontSize: 12 }}>Nothing to choose on this account.</span>}
          </div>
        </div>
      )}

      <div className="arc-row" style={{ gap: 10, marginTop: 12 }}>
        <Switch id="qr-on" checked={enabled} onChange={setEnabled} />
        <label htmlFor="qr-on" style={{ fontSize: 12.5 }}>On</label>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-sm" onClick={onDone}>Cancel</button>
        <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      <p className="arc-hint" style={{ marginTop: 8 }}>
        Only recorded calls on extensions with Auto QA are done, whatever a rule chooses.
      </p>
      {save.error && <ErrorBanner error={save.error} />}
    </form>
  );
}

// ================================================================================== setup

const PROVIDERS = [
  { key: 'deepgram', label: 'Deepgram', does: 'Transcribes each side of the call separately.' },
  { key: 'anthropic', label: 'Anthropic', does: 'Analyses the transcript (Claude).' },
  { key: 'openai', label: 'OpenAI', does: 'Analyses the transcript, instead of Anthropic.' },
] as const;

function Setup({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const who = useQaWhoami(tenantId);
  const settings = useQaSettings(tenantId);
  const save = useSaveQaSettings(tenantId);
  const keys = useQaKeys(tenantId);
  const consents = useQaConsents(tenantId);
  const mayWrite = can('qa:write');
  const mayKeys = can('qa:provider:write');
  const s = settings.data;

  const [cap, setCap] = useState<string | null>(null);
  const [maxMin, setMaxMin] = useState<string | null>(null);
  const [vocab, setVocab] = useState<string | null>(null);
  if (!s) return <TableSkeleton cols={2} />;

  const analysis = s.analysis_provider;
  const needed = ['deepgram', analysis];

  return (
    <>
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-row" style={{ gap: 10 }}>
          <Switch id="qa-on" checked={s.enabled} disabled={!mayWrite || save.isPending}
            onChange={(v) => save.mutate({ enabled: v })} />
          <label htmlFor="qa-on" style={{ fontSize: 13, fontWeight: 550 }}>
            Auto QA {s.enabled ? 'is on' : 'is off'}
            <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, fontWeight: 400, marginTop: 2 }}>
              Needs a key and an acceptance for Deepgram and for the analysis provider below.
            </span>
          </label>
        </div>
        {save.error && <ErrorBanner error={save.error} />}
      </div>

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="Providers" />
        <div className="arc-card-body">
          <div className="arc-field" style={{ maxWidth: 260 }}>
            <label className="arc-label" htmlFor="qa-analysis">Analysis</label>
            <select id="qa-analysis" className="arc-select" value={analysis} disabled={!mayWrite}
              onChange={(e) => save.mutate({ analysis_provider: e.target.value })}>
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>
          {who.data?.platform && (
            <div className="arc-row" style={{ gap: 10, margin: '6px 0 14px' }}>
              <Switch id="qa-platform" checked={s.use_platform_keys} disabled={save.isPending}
                onChange={(v) => save.mutate({ use_platform_keys: v })} />
              <label htmlFor="qa-platform" style={{ fontSize: 12.5 }}>
                Use the platform's keys where this account has none
                <span className="arc-muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>
                  The platform then pays the provider for this account's usage. Only the platform sees this.
                </span>
              </label>
            </div>
          )}
          {PROVIDERS.map((p) => (
            <ProviderRow key={p.key} tenantId={tenantId} provider={p.key} label={p.label} does={p.does}
              needed={needed.includes(p.key)} mayKeys={mayKeys}
              k={keys.data?.data.find((x) => x.provider === p.key) ?? null}
              consent={consents.data?.data.find((c) => c.provider === p.key) ?? null}
              statement={consents.data?.statements[p.key] ?? ''}
              platformFallback={s.use_platform_keys} />
          ))}
        </div>
      </div>

      <form className="arc-card" onSubmit={(e) => {
        e.preventDefault();
        save.mutate({
          ...(cap !== null ? { monthly_minutes_cap: Math.max(0, Number(cap) || 0) } : {}),
          ...(maxMin !== null ? { max_call_sec: Math.max(1, Number(maxMin) || 60) * 60 } : {}),
          ...(vocab !== null ? { vocabulary: vocab.split(/[\n,]/).map((v) => v.trim()).filter((v) => v.length >= 2) } : {}),
        }, { onSuccess: () => { setCap(null); setMaxMin(null); setVocab(null); } });
      }}>
        <CardHead title="Limits and vocabulary" />
        <div className="arc-card-body">
          <fieldset disabled={!mayWrite} style={{ border: 0, padding: 0, margin: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="qa-cap">Minutes a month</label>
                <input id="qa-cap" className="arc-input arc-num" inputMode="numeric"
                  value={cap ?? String(s.monthly_minutes_cap)} onChange={(e) => setCap(e.target.value.replace(/[^0-9]/g, ''))} />
                <div className="arc-hint">Auto QA stops at this and waits for next month or a higher limit.</div>
              </div>
              <div className="arc-field" style={{ margin: 0 }}>
                <label className="arc-label" htmlFor="qa-max">Longest call (minutes)</label>
                <input id="qa-max" className="arc-input arc-num" inputMode="numeric"
                  value={maxMin ?? String(Math.round(s.max_call_sec / 60))} onChange={(e) => setMaxMin(e.target.value.replace(/[^0-9]/g, ''))} />
                <div className="arc-hint">Longer calls are left out.</div>
              </div>
            </div>
            <div className="arc-field">
              <label className="arc-label" htmlFor="qa-vocab">Vocabulary</label>
              <textarea id="qa-vocab" className="arc-input" rows={3}
                value={vocab ?? s.vocabulary.join(', ')} onChange={(e) => setVocab(e.target.value)}
                placeholder="Product and brand names, comma separated" />
              <div className="arc-hint">Helps the transcriber spell your own words.</div>
            </div>
            <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm"
              disabled={save.isPending || (cap === null && maxMin === null && vocab === null)}>Save</button>
          </fieldset>
        </div>
      </form>
    </>
  );
}

function ProviderRow({
  tenantId, provider, label, does, needed, mayKeys, k, consent, statement, platformFallback,
}: {
  tenantId: string; provider: string; label: string; does: string; needed: boolean; mayKeys: boolean;
  k: { secret_hint: string | null; model: string | null; last_error: string | null } | null;
  consent: { accepted_at: string; accepted_by_name: string | null } | null;
  statement: string; platformFallback: boolean;
}) {
  const saveKey = useSaveQaKey(tenantId);
  const removeKey = useRemoveQaKey(tenantId);
  const accept = useAcceptQaTerms(tenantId);
  const withdraw = useWithdrawQaTerms(tenantId);
  const step = useStepUp();
  const [editing, setEditing] = useState(false);
  const [secret, setSecret] = useState('');
  const [model, setModel] = useState(k?.model ?? '');
  const [reading, setReading] = useState(false);

  const keyState = k ? `key …${k.secret_hint ?? ''}` : platformFallback ? 'the platform’s key' : 'no key';

  return (
    <div style={{ borderTop: '1px solid var(--arc-border)', padding: '12px 0' }}>
      <div className="arc-row" style={{ gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {label} {needed ? <Badge tone="info">in use</Badge> : null}
          </div>
          <div className="arc-muted" style={{ fontSize: 11.5 }}>{does}</div>
        </div>
        <div className="arc-spacer" />
        <div className="arc-stack" style={{ alignItems: 'flex-end', gap: 4, fontSize: 12 }}>
          <span>
            <Badge tone={k || platformFallback ? 'ok' : needed ? 'warn' : 'muted'}>{keyState}</Badge>
            {k?.model ? <span className="arc-subtle"> {k.model}</span> : null}
          </span>
          <span>
            <Badge tone={consent ? 'ok' : needed ? 'warn' : 'muted'}>
              {consent ? `accepted ${new Date(consent.accepted_at).toLocaleDateString('en-ZA')}` : 'not accepted'}
            </Badge>
            {consent?.accepted_by_name ? <span className="arc-subtle"> by {consent.accepted_by_name}</span> : null}
          </span>
        </div>
      </div>
      {k?.last_error && <p className="arc-hint" style={{ color: 'var(--arc-danger)' }}>{k.last_error}</p>}

      {mayKeys && (
        <div className="arc-row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : k ? 'Change key' : 'Add key'}
          </button>
          {k && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              onClick={() => { void confirmAction({ title: `Remove the ${label} key?`, action: 'Remove', danger: true }).then((ok) => { if (ok) void step.run(() => removeKey.mutateAsync(provider)); }); }}>
              Remove key
            </button>
          )}
          {!consent
            ? <button type="button" className="arc-btn arc-btn-sm" onClick={() => setReading((v) => !v)}>
                {reading ? 'Close' : 'Read and accept'}
              </button>
            : <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => { void confirmAction({ title: 'Withdraw?', body: `No further calls are sent to ${label}.`, action: 'Withdraw', danger: true }).then((ok) => { if (ok) void step.run(() => withdraw.mutateAsync(provider)); }); }}>
                Withdraw acceptance
              </button>}
        </div>
      )}

      {editing && (
        <form className="arc-row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            void step.run(() => saveKey.mutateAsync({
              provider, ...(secret ? { secret } : {}), ...(model.trim() ? { model: model.trim() } : {}),
            })).then((ok) => { if (ok) { setSecret(''); setEditing(false); } });
          }}>
          <div className="arc-field" style={{ margin: 0, minWidth: 260 }}>
            <label className="arc-label" htmlFor={`qk-${provider}`}>API key</label>
            <input id={`qk-${provider}`} className="arc-input arc-mono" type="password" autoComplete="off"
              value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={k ? 'Leave empty to keep' : ''} />
          </div>
          <div className="arc-field" style={{ margin: 0, minWidth: 180 }}>
            <label className="arc-label" htmlFor={`qm-${provider}`}>Model</label>
            <input id={`qm-${provider}`} className="arc-input arc-mono" autoComplete="off" value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={provider === 'deepgram' ? 'nova-3' : provider === 'anthropic' ? 'claude-sonnet-5' : 'gpt-4.1'} />
          </div>
          <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={saveKey.isPending || step.busy}>
            {saveKey.isPending ? 'Checking…' : 'Save'}
          </button>
        </form>
      )}

      {reading && !consent && (
        <div className="arc-card-body" style={{ background: 'var(--arc-surface-2)', borderRadius: 'var(--arc-r)', marginTop: 10 }}>
          <p style={{ fontSize: 12.5, lineHeight: 1.55 }}>{statement}</p>
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={accept.isPending || step.busy}
            onClick={() => void step.run(() => accept.mutateAsync(provider)).then((ok) => { if (ok) setReading(false); })}>
            I accept, on behalf of the account
          </button>
        </div>
      )}

      {(saveKey.error || removeKey.error || accept.error || withdraw.error) && (
        <ErrorBanner error={saveKey.error ?? removeKey.error ?? accept.error ?? withdraw.error} />
      )}
      <StepUpPrompt step={step} what={`Changing ${label} for Auto QA`}>
        A provider key is a credential, and accepting sends this account's calls to {label}.
      </StepUpPrompt>
    </div>
  );
}
